import React from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { queryClient } from '../../app/queryClient';
import { developerService, ICreatedApiKey } from '../service/developer.service';

/**
 * The account's developer API keys for one product: make one, see it once,
 * copy it, revoke it. Only rendered for an account holding the product's
 * grant; without it the store answers 404 and there would be nothing to show.
 */
export const DeveloperKeys = ({ product }: { product: string }) => {
  const { t } = useTranslation();
  const [name, setName] = React.useState('');
  const [made, setMade] = React.useState<ICreatedApiKey | null>(null);
  const [confirmRevoke, setConfirmRevoke] = React.useState<number | null>(null);

  const keysQuery = useQuery({
    queryKey: ['developer-keys', product],
    queryFn: async () => developerService.listKeys(product)
  });

  const { mutate: create, isPending: creating } = useMutation({
    mutationFn: () => developerService.createKey(product, name.trim()),
    onSuccess: (key) => {
      setMade(key);
      setName('');
      queryClient.invalidateQueries({ queryKey: ['developer-keys', product] });
    },
    onError: (err: any) => {
      toast.error(
        err.response?.data?.message || t('settings.developer.createFailed')
      );
    }
  });

  const { mutate: revoke } = useMutation({
    mutationFn: (id: number) => developerService.revokeKey(id),
    onSuccess: () => {
      setConfirmRevoke(null);
      toast.success(t('settings.developer.revoked'));
      queryClient.invalidateQueries({ queryKey: ['developer-keys', product] });
    },
    onError: () => toast.error(t('settings.developer.revokeFailed'))
  });

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('common.copied'));
    } catch {
      toast.error(t('settings.developer.copyFailed'));
    }
  };

  const keys = keysQuery.data ?? [];

  return (
    <div data-testid="developer-keys">
      <h2 className="font-body text-[18px] font-semibold text-base-black mb-[8px]">
        {t('settings.developer.title')}
      </h2>
      <p className="font-body text-[14px] text-gray-900 mb-[16px]">
        {t('settings.developer.description')}
      </p>

      {made && (
        <div className="p-[16px] rounded-[12px] mb-[16px] bg-surface border border-blue-500">
          <p className="font-body text-[13px] font-semibold text-base-black mb-[8px]">
            {t('settings.developer.copyNow')}
          </p>
          <div className="flex items-center gap-[8px]">
            <code
              data-testid="developer-key-secret"
              className="flex-1 min-w-0 truncate font-mono text-[12px] px-[12px] py-[8px] rounded-[8px] bg-surface-card border border-border-dark/[0.11]"
            >
              {made.key}
            </code>
            <button
              type="button"
              onClick={() => copy(made.key)}
              className="h-[36px] px-[16px] rounded-[32px] bg-base-black text-surface-base font-body text-[13px] font-medium hover:bg-btn-black-hover transition-all"
            >
              {t('common.copy')}
            </button>
          </div>
          <button
            type="button"
            onClick={() => setMade(null)}
            className="mt-[10px] font-body text-[13px] text-blue-link hover:underline"
          >
            {t('settings.developer.done')}
          </button>
        </div>
      )}

      <form
        className="flex gap-[8px] mb-[16px]"
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim()) create();
        }}
      >
        <input
          data-testid="developer-key-name"
          value={name}
          maxLength={64}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('settings.developer.namePlaceholder')}
          className="flex-1 min-w-0 h-[40px] px-[16px] text-[14px] font-body text-base-black bg-surface-base border border-gray-700 rounded-[24px] placeholder-gray-900 focus:outline-none focus:shadow-[0_0_0_4px_rgba(29,29,30,0.12)] transition-all"
        />
        <button
          type="submit"
          data-testid="developer-key-create"
          disabled={creating || !name.trim()}
          className={`h-[40px] px-[20px] rounded-[32px] font-body text-[13px] font-medium text-surface-base transition-all ${
            creating || !name.trim()
              ? 'bg-gray-900 opacity-50'
              : 'bg-base-black hover:bg-btn-black-hover'
          }`}
        >
          {t('settings.developer.create')}
        </button>
      </form>

      {keys.length === 0 && !keysQuery.isLoading && (
        <p className="font-body text-[13px] text-content-muted">
          {t('settings.developer.none')}
        </p>
      )}
      <ul className="flex flex-col gap-[8px]">
        {keys.map((key) => (
          <li
            key={key.id}
            data-testid={`developer-key-${key.id}`}
            className="flex items-center gap-[12px] p-[12px] rounded-[12px] bg-surface-card border border-border-dark/[0.07]"
          >
            <div className="flex-1 min-w-0">
              <p className="font-body text-[14px] font-semibold text-base-black truncate">
                {key.name}
              </p>
              <p className="font-mono text-[11px] text-content-muted truncate">
                {key.prefix}… · {t('settings.developer.created')}{' '}
                {new Date(key.createdAt).toLocaleDateString()} ·{' '}
                {key.lastUsedAt
                  ? `${t('settings.developer.lastUsed')} ${new Date(key.lastUsedAt).toLocaleDateString()}`
                  : t('settings.developer.neverUsed')}
              </p>
            </div>
            {confirmRevoke === key.id ? (
              <div className="flex gap-[6px]">
                <button
                  type="button"
                  onClick={() => revoke(key.id)}
                  className="h-[32px] px-[14px] rounded-[32px] bg-red-500 text-surface-base font-body text-[12px] font-medium hover:bg-red-600 transition-all"
                >
                  {t('settings.developer.revoke')}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmRevoke(null)}
                  className="h-[32px] px-[14px] rounded-[32px] border border-gray-500 bg-surface-base font-body text-[12px] font-medium text-base-black hover:bg-surface-secondary transition-all"
                >
                  {t('common.cancel')}
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmRevoke(key.id)}
                className="h-[32px] px-[14px] rounded-[32px] border border-red-500 text-red-500 font-body text-[12px] font-medium hover:bg-red-500 hover:text-surface-base transition-all"
              >
                {t('settings.developer.revoke')}
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};
